import React, { useCallback, useState } from 'react'
import { IUserListActionConfirmation } from 'src/components/item_segment/layouts/profiles_list/model'
import MessageBoxModal from 'src/components/message_box/message_box_with_modal'
import { textResources } from 'src/lang/de'
import {
  GroupDetailFragmentFragment,
  GroupPreviewFragmentFragment,
  useCreateGroupParticipationMutation
} from '@lopo-tech/lopo-api'
import { MSGBOX_BUTTON_TYPE_CANCEL, MSGBOX_BUTTON_TYPE_PRIMARY } from 'src/components/message_box/message_box'
import { showSnackBar } from 'src/components/snack_bar/snack_bar'
import type { ApolloError } from '@apollo/client'

type IGroup = GroupPreviewFragmentFragment | GroupDetailFragmentFragment
const labels = textResources.groups

export const useGroupJoinConfirmation = () => {
  const [messageBoxModalProps, setMessageBoxModalProps] = useState<IUserListActionConfirmation | null>()
  const [createGroupParticipation] = useCreateGroupParticipationMutation()

  const joinGroup = useCallback(
    async (group: IGroup): Promise<void> => {
      if (!group) return
      try {
        await createGroupParticipation({
          variables: { input: { groupId: group.id } }
        })
        showSnackBar(labels.joinGroupSuccess(group.name))
      } catch (err) {
        showSnackBar(labels.joinGroupError(group.name))
        console.error('joinGroup error', err as ApolloError)
      }
    },
    [createGroupParticipation]
  )

  const onGroupJoin = (group: IGroup, onConfirm?: () => void) => {
    if (joinGroup) {
      if (group.isClosed) {
        setMessageBoxModalProps({
          buttons: [
            {
              label: textResources.groups.joinClosedGroupModal.confirm,
              onClick: () => {
                setMessageBoxModalProps(null)
                joinGroup(group).then(() => {
                  if (onConfirm) onConfirm()
                })
              },
              type: MSGBOX_BUTTON_TYPE_PRIMARY
            },
            {
              label: textResources.shared.cancel,
              onClick: () => {
                setMessageBoxModalProps(null)
              },
              type: MSGBOX_BUTTON_TYPE_CANCEL
            }
          ],
          children: textResources.groups.joinClosedGroupModal.message,
          title: textResources.groups.joinClosedGroupModal.title_WEB(group.name)
        })
      } else {
        setMessageBoxModalProps(null)
        joinGroup(group).then(() => {
          if (onConfirm) onConfirm()
        })
      }
    }
  }

  return {
    ConfirmationModal: messageBoxModalProps
      ? () => <MessageBoxModal {...messageBoxModalProps}>{messageBoxModalProps.children}</MessageBoxModal>
      : null,
    onGroupJoin
  }
}
